import React, { useMemo, useState } from 'react';
import './CorezoidPanel.css';
import { sendCorezoidRequest } from '../services/corezoidApi';
import { useAuth } from '../contexts/AuthContext';

const TRADER_PROCESS_OPTIONS = [
  { value: 'orders', label: 'Orders' },
  { value: 'requisites', label: 'Requisites' },
  { value: 'devices', label: 'Devices' },
  { value: 'messages_push', label: 'Device Messages Push' },
  { value: 'messages', label: 'Messages' },
  { value: 'transactions', label: 'Transactions' }
];

const ADMIN_ONLY_PROCESS_OPTIONS = [
  { value: 'gateways', label: 'Gateways' },
  { value: 'traders', label: 'Traders' },
  { value: 'prices', label: 'Prices' },
  { value: 'banks', label: 'Banks' },
  { value: 'currencies', label: 'Currencies' }
];

const CorezoidPanel = () => {
  const { user } = useAuth();
  const processOptions = useMemo(() => {
    const baseOptions = [...TRADER_PROCESS_OPTIONS];
    if (user?.role === 'admin' || user?.isAdmin) {
      return [...baseOptions, ...ADMIN_ONLY_PROCESS_OPTIONS];
    }
    return baseOptions;
  }, [user]);

  const [formData, setFormData] = useState({
    processKey: TRADER_PROCESS_OPTIONS[0].value,
    timeout: 30,
    data: '{}'
  });
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResponse(null);

    try {
      let parsedData = {};
      if (formData.data.trim()) {
        try {
          parsedData = JSON.parse(formData.data);
        } catch {
          throw new Error('Invalid JSON format in data field');
        }
      }

      const result = await sendCorezoidRequest({
        processKey: formData.processKey,
        data: parsedData,
        timeout: parseInt(formData.timeout, 10) || 30
      });

      setResponse(result);
    } catch (err) {
      setError(err.message || 'An error occurred while sending the request');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setResponse(null);
    setError(null);
  };

  return (
    <div className="corezoid-panel">
      <div className="panel-container">
        <h2>Send Task</h2>

        <form onSubmit={handleSubmit} className="corezoid-form">
          <div className="form-group">
            <label htmlFor="processKey">
              Process <span className="required">*</span>
            </label>
            <select
              id="processKey"
              name="processKey"
              value={formData.processKey}
              onChange={handleInputChange}
              className="form-input"
            >
              {processOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <small className="form-hint">
              Internal Corezoid identifiers are resolved on the server.
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="timeout">Timeout (seconds)</label>
            <input
              type="number"
              id="timeout"
              name="timeout"
              value={formData.timeout}
              onChange={handleInputChange}
              min="1"
              max="300"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="data">Data (JSON)</label>
            <textarea
              id="data"
              name="data"
              value={formData.data}
              onChange={handleInputChange}
              placeholder='{"param1":"value1"}'
              rows="6"
              className="form-textarea"
            />
            <small className="form-hint">
              Empty object {} will be sent if this field is blank.
            </small>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !formData.processKey}
            >
              {loading ? 'Sending...' : 'Send Request'}
            </button>
            {(response || error) && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleClear}
              >
                Clear
              </button>
            )}
          </div>
        </form>

        {error && (
          <div className="response-container error">
            <h3>Error</h3>
            <pre className="response-content">{error}</pre>
          </div>
        )}

        {response && (
          <div className="response-container success">
            <h3>Response</h3>
            <div className="response-status">
              <span className={`status-badge ${response.success ? 'success' : 'error'}`}>
                {response.success ? 'Success' : 'Error'}
              </span>
            </div>
            <pre className="response-content">
              {JSON.stringify(response, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default CorezoidPanel;
